import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AlertTriangle } from "lucide-react";
import type { CatalogProduct } from "@/types";
import { UNSET_DASH } from "@/lib/format";
import { NO_STOCK_DATA_TEXT } from "@/services/api";
import { clampQty } from "@/services/pricing";
import { CategoryPill } from "@/components/CatalogTable";
import { StockPill, OutOfStockPill, stockStateOf } from "@/components/MaterialTable";
import { colors, semanticColors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

const ROW_HEIGHT = heights.pickerRow;
const HEADER_HEIGHT = heights.control;

export interface BrandCheck {
  // "unclassified" is NOT a lock verdict -- it means the product carries no
  // brand or no category this build can name, so a lock CANNOT be evaluated
  // against it. Treated like "strict" for selectability (the row is refused)
  // but it is a different fact and says so, because "you may not pick this,
  // the client has locked the brand" and "you may not pick this, nobody has
  // classified it" send someone to two different places.
  level: "strict" | "preferred" | "unclassified" | null;
  tip: string;
}

export interface StockInfo {
  /** ⚠️ THREE STATES, AND `undefined` IS ONE OF THEM. undefined means the
   *  stock source has said nothing about this product -- nobody counted, or
   *  the read failed. 0 means it DID say something: there is none. A positive
   *  number is the ordinary case.
   *
   *  It was a required `number` until slice 2.0b-ii, which is why a lookup
   *  miss had nowhere to go but 0 and every unknown product read on screen as
   *  a confident empty shelf. Do not narrow it back. */
  available?: number;
  warehouse?: string;
}

type ColKey = "select" | "partNo" | "description" | "brand" | "category" | "supplierRef" | "listPrice" | "stock" | "qty";
type ColDef = { key: ColKey; header: string; width: number; align: "left" | "right"; frozen: boolean };

const COLS: ColDef[] = [
  { key: "select",      header: "",              width:  40, align: "left",  frozen: true  },
  { key: "partNo",      header: "Part No.",      width: 150, align: "left",  frozen: true  },
  { key: "description", header: "Description",   width: 240, align: "left",  frozen: true  },
  { key: "brand",       header: "Brand",         width: 130, align: "left",  frozen: false },
  { key: "category",    header: "Category",      width: 110, align: "left",  frozen: false },
  // DISPLAY ONLY. The distributor's own SKU, so a buyer can see the number
  // the supplier's system expects on a PO. It goes no further than this cell
  // -- see CatalogProduct.supplierRef.
  { key: "supplierRef", header: "Supplier Ref",  width: 150, align: "left",  frozen: false },
  { key: "listPrice",   header: "OEM List Rs", width: 120, align: "right", frozen: false },
  { key: "stock",       header: "Stock",         width: 120, align: "left",  frozen: false },
  { key: "qty",         header: "Qty",           width: 130, align: "left",  frozen: false },
];

function frozenLefts(): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const c of COLS) {
    if (c.frozen) { out.push(acc); acc += c.width; }
    else out.push(-1);
  }
  return out;
}

export function MaterialPickerTable({
  rows,
  height,
  brandCheck,
  stockOf,
  noStockSource = false,
  selection,
  onToggle,
  onQtyChange,
  emptyMessage,
}: {
  rows: CatalogProduct[];
  height: number | string;
  brandCheck: (p: CatalogProduct) => BrandCheck;
  // ⚠️ BY PRODUCT ID, for exactly the reason `selection` below is. It took a
  // part number until slice 2.0b-ii, which meant two rows sharing one part
  // number reported a single merged quantity -- and whichever of them the
  // mock inventory map happened to write last silently won.
  stockOf: (productId: number) => StockInfo;
  // no-mock-stock-on-live: no real stock source is connected -- every stock
  // cell says "No stock data yet" instead of the unknown dash.
  noStockSource?: boolean;
  // ⚠️ KEYED BY CATALOG ID, NOT PART NUMBER. Part numbers are not unique in
  // this trade and the backend deliberately has no constraint saying they
  // are, so two rows can legitimately share one -- keying selection on it
  // would collapse them into a single pick and quantity-merge two different
  // products. The key is a string only because that is what a JS object
  // gives; the value in it is always String(product.id).
  selection: Record<string, number>;
  onToggle: (p: CatalogProduct) => void;
  onQtyChange: (productId: number, qty: number) => void;
  // WHAT AN EMPTY TABLE SAYS. Passed in rather than fixed here because only
  // the picker knows WHY it is empty -- a search that matched nothing, or a
  // filter toggle that hid everything -- and "No matching products" is the
  // wrong sentence for the second. See MaterialPicker's emptyMessage memo.
  emptyMessage: string;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const totalWidth = useMemo(() => COLS.reduce((s, c) => s + c.width, 0), []);
  const lefts = useMemo(() => frozenLefts(), []);

  return (
    <div
      data-testid="material-picker-table-scroll"
      ref={scrollRef}
      style={{
        height, overflow: "auto",
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, position: "relative", outline: "none",
      }}
    >
      <div style={{ width: totalWidth, position: "relative" }}>
        {/* HEADER */}
        <div
          data-testid="material-picker-table-head"
          style={{
            position: "sticky", top: 0, zIndex: 3, display: "flex",
            height: HEADER_HEIGHT, backgroundColor: semanticColors.panelBg,
            borderBottom: `1px solid ${semanticColors.border}`,
          }}
        >
          {COLS.map((c, idx) => (
            <div
              key={c.key}
              style={{
                width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center",
                justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
                textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                whiteSpace: "nowrap", backgroundColor: semanticColors.panelBg,
                position: c.frozen ? "sticky" : undefined,
                left: c.frozen ? lefts[idx] : undefined,
                zIndex: c.frozen ? 4 : 3,
                borderRight: c.frozen && idx === 2 ? `1px solid ${semanticColors.border}` : undefined,
              }}
            >
              {c.header}
            </div>
          ))}
        </div>

        {/* BODY */}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((v) => {
            const row = rows[v.index];
            if (!row) return null;
            const { level, tip } = brandCheck(row);
            // BOTH refusals disable the checkbox, for different reasons -- a
            // Strict lock is a client rule, an unclassified product is
            // missing master data. The tip says which.
            const disabled = level === "strict" || level === "unclassified";
            // ⚠️ BY ID. See the `selection` prop comment: part numbers are not
            // unique, so two rows sharing one would read as a single pick.
            const selKey = String(row.id);
            const picked = selKey in selection;
            const qty = selection[selKey] ?? 1;
            // BY ID, like selKey above. Was row.partNo.
            const { available, warehouse } = stockOf(row.id);
            const stockState = stockStateOf(available);
            // ⚠️ SUPPRESSED FOR UNKNOWN, SHOWN FOR A STATED 0. There is
            // nothing to warn about when nobody has counted -- the note would
            // be guessing at a shortfall. When the source says there is none,
            // ordering any quantity IS entirely sourced-in, and that is worth
            // saying, so a 0 gets the note rather than silence.
            const oversell = picked && stockState !== "unknown" && qty > available!;
            const mutedText = disabled ? semanticColors.textPlaceholder : semanticColors.textPrimary;
            const brandColor = disabled ? semanticColors.textPlaceholder : level === "preferred" ? colors.amber600 : semanticColors.textPrimary;

            return (
              <div
                key={row.id}
                data-testid={`material-picker-row-${row.id}`}
                data-part-no={row.partNo}
                data-lock-level={level ?? "none"}
                style={{
                  position: "absolute", top: 0, left: 0,
                  transform: `translateY(${v.start}px)`,
                  height: ROW_HEIGHT, width: totalWidth, display: "flex",
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: semanticColors.surface,
                }}
              >
                {COLS.map((c, idx) => {
                  const cellBase: React.CSSProperties = {
                    width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                    display: "flex", alignItems: "center",
                    justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                    fontSize: typography.fontSize.sm, whiteSpace: "nowrap",
                    position: c.frozen ? "sticky" : undefined,
                    left: c.frozen ? lefts[idx] : undefined,
                    zIndex: c.frozen ? 1 : 0,
                    backgroundColor: semanticColors.surface,
                    borderRight: c.frozen && idx === 2 ? `1px solid ${semanticColors.border}` : undefined,
                  };

                  if (c.key === "select") {
                    return (
                      <div key={c.key} style={cellBase}>
                        <input
                          type="checkbox"
                          data-testid={`picker-select-${row.id}`}
                          checked={picked}
                          disabled={disabled}
                          onChange={() => onToggle(row)}
                          style={{ accentColor: colors.indigo600, cursor: disabled ? "not-allowed" : "pointer" }}
                        />
                      </div>
                    );
                  }
                  if (c.key === "partNo") {
                    return (
                      <div key={c.key} style={{ ...cellBase, color: mutedText, fontWeight: typography.fontWeight.medium }}>
                        {row.partNo}
                      </div>
                    );
                  }
                  if (c.key === "description") {
                    return (
                      <div key={c.key} style={{ ...cellBase, color: mutedText, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.description}
                      </div>
                    );
                  }
                  if (c.key === "brand") {
                    return (
                      <div key={c.key} style={{ ...cellBase, gap: spacing[4] }}>
                        <span style={{ color: brandColor, fontWeight: level ? typography.fontWeight.medium : typography.fontWeight.regular }}>
                          {/* An unclassified product says so rather than
                              rendering an empty cell that reads as a loading
                              glitch. It is a real state -- a product nobody
                              has classified in Odoo yet -- and it is why the
                              row cannot be picked. */}
                          {row.brand ?? "Unclassified"}
                        </span>
                        {level && (
                          <span data-testid={`picker-warn-${row.id}`} className="tip-target" style={{ display: "inline-flex", alignItems: "center" }}>
                            <AlertTriangle size={iconSize.sm} strokeWidth={iconStroke.regular} color={level === "strict" ? colors.red600 : colors.amber600} />
                            <span className="tip-bubble">{tip}</span>
                          </span>
                        )}
                      </div>
                    );
                  }
                  if (c.key === "category") {
                    return (
                      <div key={c.key} style={cellBase}>
                        {/* CategoryPill has no rendering for "no category",
                            and inventing one (defaulting to Accessory, say)
                            would put a classification on a product nobody has
                            classified -- which is exactly what disarms a
                            category-scoped brand lock. The dash is the honest
                            answer and matches every other absent value. */}
                        {row.category
                          ? <CategoryPill cat={row.category} />
                          : <span style={{ color: semanticColors.textPlaceholder }}>{UNSET_DASH}</span>}
                      </div>
                    );
                  }
                  if (c.key === "supplierRef") {
                    return (
                      <div key={c.key} style={{ ...cellBase, color: semanticColors.textSecondary, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row.supplierRef ?? <span style={{ color: semanticColors.textPlaceholder }}>{UNSET_DASH}</span>}
                      </div>
                    );
                  }
                  if (c.key === "listPrice") {
                    // ⚠️ ALWAYS THE DASH, AND NOT BECAUSE THE PRICE IS
                    // UNKNOWN. anixify.product has no price column and never
                    // will -- every price in this system is computed by the
                    // engine from the six inputs on a quote line, and a
                    // catalog price would be a second place a number could
                    // come from. "Rs 0.00" here would claim the part is free;
                    // the dash says the catalog does not carry prices, which
                    // is true. The price is typed per line in the grid, where
                    // it is a negotiation rather than a catalog fact.
                    return (
                      <div
                        key={c.key}
                        data-testid={`picker-price-${row.id}`}
                        style={{ ...cellBase, color: semanticColors.textPlaceholder, fontVariantNumeric: "tabular-nums" }}
                      >
                        {UNSET_DASH}
                      </div>
                    );
                  }
                  if (c.key === "stock") {
                    return (
                      <div key={c.key} style={cellBase}>
                        {/* ⚠️ THREE STATES. The dash is now reserved for
                            UNKNOWN and means only "nothing has been said
                            about this product". It used to cover a reported
                            zero as well, so an empty shelf and an uncounted
                            one were the same mark on screen.

                            testids carry the product id, not the part number:
                            part numbers are not unique, so two rows sharing
                            one used to emit duplicate testids and a query for
                            either matched the wrong row. */}
                        {stockState === "in" && (
                          <StockPill qty={available!} warehouse={warehouse} testid={`picker-stock-${row.id}`} />
                        )}
                        {stockState === "out" && (
                          <OutOfStockPill warehouse={warehouse} testid={`picker-stock-out-${row.id}`} />
                        )}
                        {stockState === "unknown" && noStockSource && (
                          <span
                            data-testid={`picker-stock-none-${row.id}`}
                            title="No stock source is connected to this system."
                            style={{ color: semanticColors.textPlaceholder, fontSize: typography.fontSize.xs }}
                          >
                            {NO_STOCK_DATA_TEXT}
                          </span>
                        )}
                        {stockState === "unknown" && !noStockSource && (
                          <span
                            data-testid={`picker-stock-unknown-${row.id}`}
                            title="Stock level unknown — this product has no reported level."
                            style={{ color: semanticColors.textPlaceholder }}
                          >
                            —
                          </span>
                        )}
                      </div>
                    );
                  }
                  // qty
                  return (
                    <div key={c.key} style={{ ...cellBase, flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: spacing[2] }}>
                      <input
                        type="number"
                        min={1}
                        data-testid={`picker-qty-${row.id}`}
                        value={qty}
                        disabled={!picked}
                        onChange={(e) => onQtyChange(row.id, clampQty(parseFloat(e.target.value)))}
                        style={{
                          width: 64, height: heights.input, padding: `0 ${spacing[8]}px`,
                          fontSize: typography.fontSize.base, textAlign: "right", fontVariantNumeric: "tabular-nums",
                          color: semanticColors.textPrimary,
                          backgroundColor: picked ? semanticColors.surface : semanticColors.panelBg,
                          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                      {oversell && (
                        <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder, lineHeight: typography.lineHeight.tight }}>
                          Only {available} in stock — remainder will be sourced.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="material-picker-empty"
              style={{
                position: "absolute", top: 0, left: 0, right: 0,
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              {emptyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
